import JenErrorToast from '@/components/ui/jen-error-toast';
import useInitializeData from '@/hooks/useInitializeProject';
import { SessionType, useSessionStore } from '@/hooks/useSessionStore';
import { EditorRequestDTO, postAudioGenerateEditor } from '@/service/editor';
import { postAudioUploadForTrim } from '@/service/generator';
import { postAudioGenerateReimagine, postAudioGenerateV2 } from '@/service/generatorv2';
import { useAppStore } from '@/state/appStore';
import { useAudioListStore } from '@/state/audio-list-store';
import useEditorStore, { EditorOperatorEnum } from '@/state/editor-store';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import useGeneratorStore, { FormValues, GeneratorTypeEnums, defaultGeneratorFormValues } from '@/state/generator-store';
import { RoleEnum } from '@/types/global';
import { convertSecondsToMilliseconds } from '@/utils/time';
import { useRequest } from 'ahooks';
import React, { useEffect, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { DefaultQualityValueMap } from './components/SimpleEffects';
import { usePastePrompt } from './hooks/usePastePrompt';
import { prepareEditorParams, prepareGeneratorParams } from './utils';
import { useShowBonusTrackDialog } from '@/hooks/useShowBonusTrackDialog';
import { titleCaseToKebabCase } from '@/utils';
import { useRenewFilterModal } from './components/SimpleEffects/AddFilterIcon/useRenewFilterModal';
import { useNavigate } from 'react-router-dom';

const updateSessionCredit = (session: SessionType, creditBalance: number) => {
  return {
    ...session,
    user: {
      ...session.user,
      credit: {
        ...session.user.credit,
        value: creditBalance,
      },
    },
  };
};

export const useModel = () => {
  const {
    editorType,
    generatorType,
    editorSelectedInstrument,
    setGeneratorResult,
    formValues,
    setFormValues,
    register,
    setRegister,
    setControl,
    hummingAudioSelected,
    audioInfo,
    setGeneratorForm,
    isGeneratorDrawerShow,
    setIsGeneratorDrawerShow,
    uploadFileData,
    tracksCount,
    setGeneratorType,
    generatorForm,
  } = useGeneratorStore();

  const form = useForm<FormValues>({
    defaultValues: formValues,
    mode: 'onChange',
  });

  const {
    control: currentControl,
    register: currentRegister,
    formState: { errors },
    getValues,
    watch,
  } = form;

  const { currentFilterGroup, setCurrentFilterGroup, setDisabled } = useGeneratorFilterStore();

  const {
    field: { onChange: setStyleFilterWeight, value: filterWeight },
  } = useController({
    name: 'filterWeight',
    control: currentControl,
    defaultValue: formValues?.filterWeight || 0,
  });

  const {
    field: { onChange: setStyleFilterId, value: styleFilterId },
  } = useController({
    name: 'styleFilterId',
    control: currentControl,
  });

  const {
    field: { onChange: onChangeStyleFilterIsOpened, value: styleFilterIsOpened },
  } = useController({
    name: 'styleFilterIsOpened',
    control: currentControl,
  });

  const navigate = useNavigate();

  const {
    open,
    close,
    Modal: RenewFilterModal,
  } = useRenewFilterModal({
    onRenew: () => {
      if (currentFilterGroup?.name) {
        navigate(`/marketplace/filter/${titleCaseToKebabCase(currentFilterGroup?.name)}`);
      }
    },
    onRemove: () => {
      setCurrentFilterGroup(null);
      onChangeStyleFilterIsOpened(false);
      setStyleFilterId(undefined);
      setStyleFilterWeight(undefined);
    },
  });

  const { session, setSession } = useSessionStore();

  const qualityDefaultValue = useMemo((): string => {
    if (session?.user?.plan?.key) {
      const value = DefaultQualityValueMap[session.user.plan.key as RoleEnum];
      return value;
    } else {
      return 'mp3';
    }
  }, [session?.user?.plan?.key]);

  const {
    editor,
    duration: editorDuration,
    operator,
    setTrimLoading,
  } = useEditorStore((state) => ({
    editor: state.editor,
    duration: state.duration,
    operator: state.operator,
    setTrimLoading: state.setTrimLoading,
  }));

  const { pane } = useAppStore((state) => ({
    pane: state.pane,
  }));

  const { addPastePrompt } = usePastePrompt();

  const { showDialog: showBonusTrackDialog } = useShowBonusTrackDialog();

  const { userGuideManager } = useInitializeData();
  const { resetAllGuides } = userGuideManager;

  useEffect(() => {
    if (pane) {
      const formDataBtn = pane.addButton({
        title: 'formData',
        label: 'formData',
      });

      formDataBtn.on('click', () => {
        const data = getValues();
        console.info('Form Data:', data);
      });

      const generateParamBtn = pane.addButton({
        title: 'generate param',
        label: 'generate param',
      });

      generateParamBtn.on('click', () => {
        const data = getValues();
        if (generatorType === GeneratorTypeEnums.EDITOR && operator && editor) {
          const param = prepareEditorParams({
            data,
            editor,
            audioInfo,
            editorDuration,
            operator,
            tracksCount,
            qualityDefaultValue,
          });
          console.info('Param', param);
        } else {
          const param = prepareGeneratorParams({
            data,
            type: generatorType,
            editorType,
            editorSelectedInstrument,
            hummingAudioSelected,
            generatorType,
            tracksCount,
            qualityDefaultValue,
          });
          console.info('Param', param);
        }
      });

      const resetWelcomeBtn = pane.addButton({
        title: 'reset all welcome',
        label: 'reset all welcome',
      });

      resetWelcomeBtn.on('click', () => {
        resetAllGuides();
      });

      return () => {
        pane.remove(formDataBtn);
        pane.remove(generateParamBtn);
        pane.remove(resetWelcomeBtn);
      };
    }
  }, [
    pane,
    getValues,
    generatorType,
    editor,
    audioInfo,
    editorDuration,
    operator,
    editorType,
    editorSelectedInstrument,
    hummingAudioSelected,
  ]);

  useEffect(() => {
    setControl(currentControl);
    setRegister(currentRegister);
    setGeneratorForm(form);
  }, [currentControl, currentRegister, setControl, setRegister, setGeneratorForm]);

  const { run: runPostAudioGenerate, loading: generatorTextLoading } = useRequest(postAudioGenerateV2, {
    manual: true,
    onSuccess: (data) => {
      // if has bonus track
      if (data?.data?.some(({ is_bonus }) => is_bonus)) {
        showBonusTrackDialog();
      }
      setSession(updateSessionCredit(session, data.creditBalance));
      setGeneratorResult(data);
      setGeneratorType(GeneratorTypeEnums.TEXT);
      setIsGeneratorDrawerShow(false);
    },
    onError: (error: Error & { errorCode?: string; errorMessage?: string; statusCode?: number; timestamp?: string }) => {
      if (error?.errorCode === 'STYLEFILTER_NOT_AVAILABLE' || error?.errorCode === 'STYLEFILTER_CREDIT_NOT_ENOUGH') {
        return open();
      }
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    },
    debounceWait: 500,
  });

  const { run: runPostReimagineGenerate, loading: generateReimagineLoading } = useRequest(postAudioGenerateReimagine, {
    manual: true,
    onSuccess: (data) => {
      setSession(updateSessionCredit(session, data.creditBalance));
      setGeneratorResult(data);
    },
    onError: (error: Error & { errorCode?: string; errorMessage?: string; statusCode?: number; timestamp?: string }) => {
      if (error?.errorCode === 'STYLEFILTER_NOT_AVAILABLE' || error?.errorCode === 'STYLEFILTER_CREDIT_NOT_ENOUGH') {
        return open();
      }
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    },
    debounceWait: 500,
  });

  const { run: runPostAudioGenerateEditor, loading: generateEditorLoading } = useRequest(postAudioGenerateEditor, {
    manual: true,
    onSuccess: (data) => {
      setSession(updateSessionCredit(session, data.creditBalance));
      setGeneratorResult(data);
      setGeneratorType(GeneratorTypeEnums.TEXT);
      setIsGeneratorDrawerShow(false);
      setDisabled(false); // reset SF disabled state
      editor?.destroy();
    },
    onError: (error: Error & { errorCode?: string; errorMessage?: string; statusCode?: number; timestamp?: string }) => {
      if (error?.errorCode === 'STYLEFILTER_NOT_AVAILABLE' || error?.errorCode === 'STYLEFILTER_CREDIT_NOT_ENOUGH') {
        return open();
      }
      toast(<JenErrorToast error={error} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    },
    debounceWait: 500,
  });

  const { runAsync: postAudioUploadForTrimRun } = useRequest(postAudioUploadForTrim, {
    manual: true,
    onSuccess: (data) => {
      setGeneratorResult(data);
      setGeneratorType(GeneratorTypeEnums.TEXT);
      setIsGeneratorDrawerShow(false);
    },
  });

  const { audioList, setAudioList } = useAudioListStore((state) => ({
    audioList: state.audioList,
    setAudioList: state.setAudioList,
  }));

  const onSubmit = React.useCallback(async () => {
    const data = getValues();

    if (!data.folderId) {
      data.folderId = null;
    }
    // if (!validateForm(data)) {
    //   return;
    // }

    if (generatorType === GeneratorTypeEnums.TEXT) {
      const params = prepareGeneratorParams({
        data,
        type: GeneratorTypeEnums.TEXT,
        editorType,
        editorSelectedInstrument,
        hummingAudioSelected,
        generatorType,
        tracksCount,
        qualityDefaultValue,
      });
      runPostAudioGenerate(params as any);
    }

    if (generatorType === GeneratorTypeEnums.R3IMAGINE) {
      const params = prepareGeneratorParams({
        data,
        type: GeneratorTypeEnums.R3IMAGINE,
        editorType,
        editorSelectedInstrument,
        hummingAudioSelected,
        generatorType,
        uploadFileData,
        tracksCount,
        qualityDefaultValue,
      });
      runPostReimagineGenerate(params as any);
    }

    if (generatorType === GeneratorTypeEnums.EDITOR && editor && operator) {
      generatorForm?.reset(defaultGeneratorFormValues);
      if (operator === EditorOperatorEnum.TRIM) {
        const { start, end } = editor?.regions?.getRegions()?.[0] ?? {
          start: 0,
          end: 0,
        };
        setIsGeneratorDrawerShow(false);
        const mockId = `fe_track_trim_${Math.random()}`;
        // before trim successful, unshift a mock trim audiItem data in AudioList
        setAudioList((audioList) => {
          return [
            {
              id: mockId,
              name: audioInfo?.name,
              prompt: audioInfo?.name,
              is_generated: false,
              duration: audioInfo.duration,
              folder_id: audioInfo.folder_id,
              metadata: { type: 'trim' },
            },
            ...audioList,
          ];
        });

        try {
          const audioBlob = await editor?.cut(start, end);
          if (!audioBlob) {
            throw new Error('trim error');
          }
          const formData = new FormData();
          formData.append('file', audioBlob);
          formData.append('startTime', String(convertSecondsToMilliseconds(start)));
          formData.append('endTime', String(convertSecondsToMilliseconds(end)));
          formData.append('type', audioBlob?.type);
          formData.append('audioId', audioInfo?.id);
          await postAudioUploadForTrimRun(formData);
        } catch (error: any) {
          setAudioList((audioList) => {
            return [...audioList].filter(({ id }) => {
              return id !== mockId;
            });
          });
          toast(<JenErrorToast error={error} />, {
            className: '!border-0 !shadow-none',
            unstyled: true,
          });
        }
      } else {
        if (data?.styleFilterIsOpened && data?.styleFilterId && !localStorage.getItem('hasShowEditTrackFromSfTip')) {
          localStorage.setItem('hasShowEditTrackFromSfTip', 'true');
          toast.success('This Feature Uses StyleFilter Minutes', {
            description: 'StyleFilter™ tracks that use Intro, Refill, Extend, and Outro will deduct StyleFilter™ minutes',
          });
        }

        const params = prepareEditorParams({
          data,
          editor,
          audioInfo,
          editorDuration,
          operator,
          tracksCount,
          qualityDefaultValue,
          currentFilterGroup,
          filterWeight,
        });
        runPostAudioGenerateEditor(params as EditorRequestDTO);
      }
    }

    const { prompt } = data;
    if (prompt) {
      addPastePrompt(prompt);
    }
  }, [
    getValues,
    editor,
    audioInfo,
    editorDuration,
    operator,
    editorType,
    editorSelectedInstrument,
    hummingAudioSelected,
    generatorType,
    uploadFileData,
    runPostAudioGenerate,
    runPostReimagineGenerate,
    runPostAudioGenerateEditor,
    postAudioUploadForTrimRun,
    addPastePrompt,
  ]);

  const onChange = (data: FormValues) => {
    setFormValues(data);
  };

  const generatorLoading = useMemo(() => {
    if (generatorType === GeneratorTypeEnums.TEXT) {
      return generatorTextLoading;
    }
    if (generatorType === GeneratorTypeEnums.R3IMAGINE) {
      return generateReimagineLoading;
    }
    if (generatorType === GeneratorTypeEnums.EDITOR) {
      return generateEditorLoading;
    }
  }, [generatorType, generatorTextLoading, generateReimagineLoading, generateEditorLoading]);

  return {
    isGeneratorDrawerShow,
    setIsGeneratorDrawerShow,
    register,
    form,
    formState: { errors },
    watch,
    // reset,
    onChange,
    onSubmit,
    generatorLoading,
    RenewFilterModal,
    // loading,
  };
};
